// ─── UI String Keys ──────────────────────────────────────────────────────────
// Canonical set of translatable UI strings for the guest app.
// English values serve as defaults when no custom translation exists.

export const UI_STRING_KEYS = {
  // ── Landing page ───────────────────────────────────────────
  welcome_to: 'Welcome To',
  browse_menu: 'Browse All Items & Order',
  ready_to_order: 'Ready to order?',
  // Shown when a guest opens the public link of a venue that does not run a venue QR —
  // a hotel serving only its rooms. The venue and its menus still show; only ordering is
  // withheld, because an order from here has no table and no room to go to.
  browse_only_scan_to_order: 'Browse the menu here. To order, scan the QR code in your room.',
  our_menus: 'Our Menus',
  browse_by_category: 'Browse by Category',
  quick_actions: 'Quick Actions',
  our_location: 'Our Location',
  get_in_touch: 'Get In Touch',
  checked_in: 'Checked In',
  venue_location: 'Venue Location',
  view_active_orders: 'View Active Orders',
  order_in_progress: 'Order in Progress',
  ready_for_pickup: 'Ready for Pickup!',
  track: 'Track',
  membership: 'Membership',
  track_your_history: 'Track your history',
  welcome_back: 'Welcome back',
  sign_in: 'Sign In',
  profile: 'Profile',

  // ── Menu page ──────────────────────────────────────────────
  menu: 'Menu',
  discover_collection: 'Discover our collection',
  search_placeholder: 'What are you craving?',
  no_items_found: 'No items found',
  no_items_found_desc: 'Try searching for something else or browse categories.',
  clear_filters: 'Clear active filters',
  items: 'Items',

  // ── Item detail ────────────────────────────────────────────
  the_experience: 'The Experience',
  select_option: 'Select Option',
  special_requests: 'Special Requests',
  special_requests_placeholder: 'Any dietary requirements or preferences?',
  add_to_order: 'Add to Order',
  view_cart: 'View Cart',
  swipe_for_more: 'Swipe for more',
  chefs_choice: "Chef's Choice",
  item_not_found: 'Item Not Found',
  return_to_menu: 'Return to Menu',
  customize: 'Customize',
  required: 'Required',
  select_at_least: 'Select at least',
  select_up_to: 'Select up to',
  included_free: 'included free',
  selected: 'selected',
  free: 'Free',

  // ── Cart ───────────────────────────────────────────────────
  your_cart: 'Your Cart',
  cart_empty: 'Your cart is empty',
  cart_empty_desc: 'Browse the menu and add items to get started',
  browse_menu_cta: 'Browse Menu',
  subtotal: 'Subtotal',
  service_fee: 'Service Fee',
  total: 'Total',
  proceed_to_checkout: 'Proceed to Checkout',

  // ── Checkout ───────────────────────────────────────────────
  checkout: 'Checkout',
  order_type: 'Order Type',
  dine_in: 'Dine In',
  counter_pickup: 'Counter Pickup',
  table_number: 'Table Number',
  table_number_placeholder: 'Enter your table number',
  auto_detected_qr: 'Auto-detected from QR scan',
  your_name: 'Your Name (Optional)',
  your_name_placeholder: 'Enter your name',
  your_phone: 'Phone (Optional)',
  your_phone_placeholder: 'e.g. +971 50 123 4567',
  order_notes: 'Order Notes (Optional)',
  order_notes_placeholder: 'Any special instructions?',
  order_summary: 'Order Summary',
  continue_to_payment: 'Continue to Payment',

  // ── Location ordering (In-Room Dining) ─────────────────────
  // The venue's location label ("Room", "Villa"...) is owner-set free text,
  // interpolated into these strings — never translated itself.
  in_room_dining: 'In-Room Dining',
  delivered_to_you: 'Order & pay online — delivered straight to you',
  // The same invitation for a venue that does not take online payments: the order is
  // paid when it arrives (see acceptsOnlinePayment).
  delivered_to_you_pay_on_delivery: 'Order now, pay on delivery — brought straight to you',
  location_number_q: "What's your",
  location_number_placeholder: 'e.g. 1204',
  confirm_location: 'Continue',
  payment_processing: 'Confirming your payment…',
  pay_on_delivery: 'Pay on Delivery',
  pay_on_delivery_desc: 'Pay when your order arrives — cash, card, or at the front desk',

  // ── Payment ────────────────────────────────────────────────
  payment: 'Payment',
  bill_summary: 'Bill Summary',
  add_a_tip: 'Add a Tip',
  tip: 'Tip',
  tip_added: 'tip added',
  custom: 'Custom',
  payment_method: 'Payment Method',
  pay_at_counter: 'Pay at Counter',
  pay_at_counter_desc: 'Cash or card at the counter',
  pay_online: 'Pay Online',
  pay_online_desc: 'Card, Apple Pay, or Google Pay',
  place_order: 'Place Order',
  placing_order: 'Placing Order...',
  proceed_to_pay: 'Proceed to Pay',
  setting_up_payment: 'Setting up payment...',

  // ── Order tracking ─────────────────────────────────────────
  your_order: 'Your Order',
  order_received: 'Order Received',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  completed: 'Completed',
  order_cancelled: 'Order Cancelled',
  order_complete: 'Order Complete',
  kitchen: 'Kitchen',
  bar: 'Bar',
  queue_position: 'Queue Position',
  estimated_wait: 'Estimated wait',
  updates_automatically: 'Updates automatically every 10 seconds',
  rate_your_experience: 'Rate Your Experience',
  collect_at_counter: 'Please collect your order at the counter',
  partially_ready: 'Partially Ready',

  // ── Active orders ──────────────────────────────────────────
  active: 'Active',
  past: 'Past',
  no_active_orders: 'No active orders',
  no_past_orders: 'No past orders',
  place_order_to_track: 'Place an order from the menu to track it here',
  completed_orders_here: 'Your completed orders will appear here',
  track_order: 'Track Order',

  // ── Review ─────────────────────────────────────────────────
  how_was_experience: 'How was your experience?',
  sorry_to_hear: "We're sorry to hear that",
  thanks_feedback: 'Thanks for your feedback',
  glad_you_enjoyed: 'Glad you enjoyed it!',
  what_did_you_love: 'What did you love?',
  what_could_be_better: 'What could be better?',
  additional_comments: 'Additional Comments (Optional)',
  comments_placeholder: 'Tell us more about your experience...',
  submit_review: 'Submit Review',
  thank_you: 'Thank You!',
  feedback_helps: 'Your feedback helps us improve',
  rate_on_google: 'Rate us on Google',
  back_to_home: 'Back to Home',

  // ── AI Assistant ───────────────────────────────────────────
  ai_menu_guide: 'AI Menu ',
  ai_ask_anything: 'Ask me anything about the menu',
  what_are_you_craving: 'What are you craving?',
  ai_help_text: 'I can help you find dishes, check dietary info, suggest pairings, or build you a complete meal.',
  ai_input_placeholder: 'Ask about ingredients, calories, pairings...',
  items_in_cart: 'items in cart',

  // ── Bottom nav ─────────────────────────────────────────────
  home: 'Home',
  orders: 'Orders',
  history: 'History',
  map: 'Map',
  ai_guide: 'AI Menu',

  // ── Common ─────────────────────────────────────────────────
  browse: 'Browse',
  back: 'Back',
  close: 'Close',
  loading: 'Loading...',
  go_back: 'Go Back',
  go_home: 'Go Home',
  try_again: 'Try Again',
  error_loading_menu: 'Error Loading Menu',
  venue_not_found: 'Venue Not Found',
  venue_unavailable: 'This venue doesn\'t exist or is unavailable.',
  temporarily_closed: 'Temporarily Closed',
  venue_currently_closed: 'This venue is currently closed',
  opens: 'Opens',
  categories: 'categories',
  open: 'Open',
  closed: 'Closed',
  mins: 'mins',
  kcal: 'kcal',
  wifi: 'Wi-Fi',
  support: 'Support',
  find_us: 'Find Us',
  about: 'About',
  guest_wifi: 'Guest Wi-Fi',
  network_name: 'Network Name',
  password: 'Password',
  available_for_calls: 'Available for calls',
  no_phone: 'No Phone',
  address_not_set: 'Address not set',
  self_service: 'Self Service',
  waiter_service: 'Waiter Service',
  hybrid_service: 'Hybrid',
  table: 'Table',
  contains: 'Contains',
  upgrade_to_add: 'Upgrade to add special requests',

  // ── Profile page ───────────────────────────────────────────
  sign_out: 'Sign Out',
  order_history: 'Order History',
  view_past_orders: 'View past orders and bookings',
  alerts: 'Alerts',
  notifications_promotions: 'Notifications and promotions',
  preferences: 'Preferences',
  theme: 'Theme',
  dark_mode: 'Dark mode',
  light_mode: 'Light mode',
  dark: 'Dark',
  light: 'Light',
  language: 'Language',
  menu_interface_language: 'Menu & interface language',
  why_sign_in: 'Why Sign In?',
  track_order_history: 'Track your order history',
  manage_bookings: 'Manage upcoming bookings',
  personalized_offers: 'Get personalized offers & alerts',
  faster_checkout: 'Faster checkout experience',
  continue_with_google: 'Continue with Google',
  signing_in: 'Signing in...',

  // ── About page ─────────────────────────────────────────────
  about_us: 'About Us',
  contact: 'Contact',
  follow_us: 'Follow Us',
  need_help: 'Need Help?',
  contact_support: 'Contact Support',
  powered_by: 'Powered by GatheredHQ',

  // ── Support page ───────────────────────────────────────────
  venue_support: 'Venue Support',
  platform_support: 'Platform Support',
  platform_support_desc: 'For issues with the app, payments, or your account, contact the GatheredHQ team.',
  common_questions: 'Common Questions',
  faq_order_q: 'How do I place an order?',
  faq_order_a: 'Browse the menu, add items, and proceed to checkout.',
  faq_modify_q: 'Can I modify my order?',
  faq_modify_a: 'Contact staff directly for any order modifications.',
  faq_payment_q: 'Is my payment secure?',
  faq_payment_a: 'Yes, all payments are processed through Stripe with bank-level encryption.',
  venue_email: 'Venue email',
  no_venue_email: 'No venue email available',
  tap_to_call: 'Tap to call',
  wifi_not_available: 'Wi-Fi details are not available for this venue.',
  need_help_desc: 'Need help? Reach out to us and we\'ll get back to you as soon as possible.',

  // ── Alerts page ────────────────────────────────────────────
  no_notifications: 'No notifications yet',
  notifications_will_appear: 'Order status updates will appear here when you place an order.',
  just_now: 'Just now',

  // ── History page ───────────────────────────────────────────
  recent_orders: 'Recent Orders',
  history_empty_signed_out: 'Sign in and upgrade to track your orders and bookings.',
  history_empty: 'Your orders, bookings, and activity will appear here.',
  address: 'Address',
  address_not_available: 'Address not available',

  // ── Map page ───────────────────────────────────────────────
  venue_map: 'Find Us',
  get_directions: 'Get Directions',

  // ── Cart & checkout (coverage sweep 2026-09-06) ────────────
  items_unavailable_title: 'Some items are no longer available',
  items_unavailable_hint: 'Remove unavailable items to proceed to checkout.',
  out_of_stock: 'Out of Stock',
  remove_unavailable_items: 'Remove unavailable items',
  remove: 'Remove',

  // ── Order mode selector (coverage sweep 2026-09-06) ────────
  select_preferred_option: 'Please select your preferred option',
  dine_in_desc: "Eat at the restaurant, pay after you're served",
  takeaway: 'Takeaway',
  takeaway_desc: 'Order and pick up at the counter, pay now',
  whats_your_table_number: "What's your table number?",
  check_table_card: 'Check the table card or ask your waiter',
  continue_action: 'Continue',
  enter_table_later: "I'll enter it later",
  special_request: 'Special request',
  special_request_placeholder: 'Special request (e.g., no onion, extra spicy)',
  orders_not_accepted_now: 'Orders cannot be placed at this time.',
  privacy_policy: 'Privacy Policy',

  // ── coverage sweep ──
  no_payment_was_taken_nothing: 'No payment was taken — nothing will be charged.',
  reason_optional: 'Reason (optional)',
  keep_order: 'Keep Order',
  cancelling: 'Cancelling…',
  order_not_found: 'Order Not Found',
  youre_sharing_this_table: "You're sharing this table",
  all_orders_at_this_merged: 'All orders at this merged table count toward the same bill.',
  some_guests_have_paid_their: 'Some guests have paid their share. Pay remaining to complete.',
  pay_remaining: 'Pay Remaining',
  paid_online: 'Paid Online',
  your_payment_has_been_confirmed: 'Your payment has been confirmed',
  payment_collected: 'Payment Collected',
  thank_you_for_dining_with: 'Thank you for dining with us',
  order_more: 'Order More',
  add_more_items_to_your: 'Add more items to your table',
  bill_requested: 'Bill Requested',
  your_waiter_will_be_with: 'Your waiter will be with you shortly to collect payment',
  your_order_will_reach_the: 'Your order will reach the kitchen as soon as payment is confirmed',
  please_proceed_to_the_counter: 'Please proceed to the counter to complete payment',
  cancel_this_order: 'Cancel this order',
  changed_my_mind_ordered_wrong: 'Changed my mind, ordered wrong item…',
  cancel_window_explainer: 'You can only cancel before the kitchen starts preparing your order. Once the kitchen accepts it, cancellation will no longer be available.',

  // ── coverage sweep ──
  loading_your_bill: 'Loading your bill',
  just_a_moment: 'Just a moment…',
  couldnt_load_your_bill: "Couldn't load your bill",
  go_back_2: 'Go back',
  all_settled: 'All settled!',
  view_order_status: 'View order status',
  your_bill: 'Your Bill',
  outstanding_balance: 'Outstanding balance',
  remaining: 'Remaining',
  add_a_tip_2: 'Add a tip',
  no_tip: 'No tip',
  total_to_pay: 'Total to pay',
  split_the_bill: 'Split the Bill',
  email_for_receipt_optional: 'Email for receipt (optional)',

  // ── coverage sweep ──
  your_orders_and_bookings: 'Your orders and bookings',
  sign_up: 'Sign Up',
  upcoming_bookings: 'Upcoming Bookings',
  no_orders_yet: 'No orders yet',
  past_bookings: 'Past Bookings',
  no_upcoming_bookings: 'No upcoming bookings',
  browse_events: 'Browse Events',
  no_past_bookings: 'No past bookings',
  sign_in_or_create_an: 'Sign in or create an account to see your orders and bookings — saved to your account and kept across every visit.',

  // ── coverage sweep ──
  sign_in_to_your_account: 'Sign in to your account to continue',
  forgot_password: 'Forgot password?',
  create_account: 'Create account',
  enter_your_password: 'Enter your password',

  // ── coverage sweep ──
  live_vibe: 'Live Vibe',
  detailed_status: 'Detailed Status',
  occupied: 'Occupied',
  available: 'Available',
  reserved: 'Reserved',
  in_queue: 'In Queue',
  est_wait: 'Est. Wait',

  // ── coverage sweep ──
  dine_in_2: 'Dine-in',
  want_to_change_order_type: 'Want to change order type?',
  changing_your_order_type_will: 'Changing your order type will remove all items from your cart',
  cancel: 'Cancel',
  confirm: 'Confirm',
  you_will_need_to_start: 'You will need to start your order again from the beginning. Are you sure you want to continue?',

  // ── coverage sweep ──
  create_account_2: 'Create Account',
  join_us: 'Join us',
  create_an_account_to_track: 'Create an account to track orders and get personalized offers',
  first_name: 'First Name',
  last_name: 'Last Name',
  terms_conditions: 'Terms & Conditions',
  creating_account: 'Creating account...',
  sign_in_2: 'Sign in',
  min_6_characters: 'Min. 6 characters',

  // ── coverage sweep ──
  split_the_bill_2: 'Split the bill',
  select_all: 'Select all',
  all_items_covered: 'All items covered',
  every_item_on_this_bill: 'Every item on this bill has been claimed',
  split_between_how_many: 'Split between how many?',
  each_person_pays: 'Each person pays',
  how_much_will_you_pay: 'How much will you pay?',
  your_share: 'Your share',
  pay_my_share: 'Pay My Share',
  your_name_2: 'Your name',

  // ── coverage sweep ──
  total_amount: 'Total Amount',
  guests: 'Guests',
  number_of_people: 'Number of People',
  your_items: 'Your Items',
  enter_amount_to_pay: 'Enter Amount to Pay',
  your_share_2: 'Your Share',

  // ── coverage sweep ──
  meet_your_ai_menu: 'Meet your AI Menu',
  just_ask: 'Just ask.',
  maybe_later: 'Maybe later',
  always_one_tap_away_in: 'Always one tap away in your menu bar',
  dismiss: 'Dismiss',
  tell_me_what_you_apos: 'Tell me what you&apos;re craving — diet, budget or mood — and I&apos;ll build your order in seconds.',
  mobile_number: 'Mobile Number',
  optional_paren: '(optional)',
  dont_scroll: "Don't scroll.",
  try_the_ai_menu: 'Try the AI Menu',

  // ── coverage sweep ──
  failed_to_load: 'Failed to Load',
  sold_out: 'Sold Out',
  join_the_waitlist: 'Join the Waitlist',
  join_the_waitlist_blurb: 'Leave your details and we will let you know the moment a place opens up.',
  youre_on_the_list: "You're on the list",
  well_let_you_know_if_a_place_opens: 'We will be in touch as soon as a place opens up.',
  waitlist_contact_hint: 'A phone number or an email — whichever suits you.',
  this_event_is_fully_booked: 'This event is fully booked.',
  reserve_your_spot: 'Reserve your spot',
  book_this_event: 'Book this event',
  add_guests_above_to_see: 'Add guests above to see available tables.',
  non_refundable: 'Non-refundable',
  refundable: 'Refundable',
  choose_your_date: 'Choose your date',
  how_many_guests: 'How many guests?',
  your_details: 'Your details',

  // ── coverage sweep ──
  no_booking_selection: 'No Booking Selection',
  please_go_back_and_select: 'Please go back and select your booking details first.',
  getting_everything_ready_for_you: 'Getting everything ready for you…',
  booking_failed: 'Booking Failed',
  one_last_step: 'One last step',
  almost_yours: 'Almost yours',
  your_details_2: 'Your Details',
  payment_summary: 'Payment Summary',
  secured_by_ziina_youll_be: "Secured by Ziina · You'll be redirected to pay",
  non_refundable_this_booking_cannot: 'Non-refundable: This booking cannot be cancelled or refunded',

  // ── coverage sweep ──
  back_to_venue: 'Back to Venue',
  booking_not_found: 'Booking Not Found',
  youre_all_booked: 'You’re all booked!',
  booking_code: 'Booking Code',
  show_this_code_at_the: 'Show this code at the venue',
  booked_by: 'Booked By',
  copy_code: 'Copy Code',

  // ── coverage sweep ──
  events_offers: 'Events & Offers',
  no_events_available: 'No Events Available',
  check_back_soon_for_upcoming: 'Check back soon for upcoming events and offers.',
  free_entry: 'Free Entry',
  email_address: 'Email Address',

  // ── coverage sweep ──
  tap_any_available_date: 'Tap any available date',

  // ── coverage sweep ──
  deal_price: 'Deal price',
  bundle_price: 'Bundle Price',
  instant_confirmation_no_wait: 'Instant confirmation — no wait',
  pricing: 'Pricing',
  whats_included: "What's Included",
  buffet_menu: 'Buffet Menu',

  // ── coverage sweep ──
  terms_agreements: 'Terms & Agreements',
  venue_terms_amp_conditions: 'Venue Terms &amp; Conditions',
  gatheredhq_terms_of_service: 'GatheredHQ Terms of Service',
  i_accept: 'I Accept',
  id_love_to_receive_exclusive: "I'd love to receive exclusive news, offers, and early updates from the venue",

  // ── coverage sweep ──
  no_image_available: 'No image available',
  this_item_is_currently_unavailable: 'This item is currently unavailable',
  drizzle: 'Drizzle:',
  often_ordered_with: 'Often ordered with',
  people_usually_order_these_too: 'People usually order these too',
  currently_unavailable: 'Currently Unavailable',
  decrease_quantity: 'Decrease quantity',
  increase_quantity: 'Increase quantity',
  previous: 'Previous',

  // ── coverage sweep ──
  we_use_cookies: 'We use cookies',
  cookie_policy: 'Cookie Policy',
  decline: 'Decline',
  accept_all: 'Accept All',
  we_use_essential_cookies_to: 'We use essential cookies to keep the app running and analytics cookies to improve your experience.',

  // ── coverage sweep ──
  select_a_date_and_guests: 'Select a date and guests to see available tables.',

  // ── Floor plan (guest table picker) ────────────────────────
  pick_your_table: 'Pick your table',
  close_the_floor_plan: 'Close the floor plan',
  fit_the_whole_room: 'Fit the whole room',
  whole_room: 'Whole room',
  taken: 'Taken',
  your_pick: 'Your pick',
  zoom_in: 'Zoom in',
  zoom_out: 'Zoom out',
  no_free_tables_try_another: 'No free tables for this time. Try another time or date.',
  tap_a_table_on_the_plan: 'Tap a table on the plan',
  free_pinch_to_zoom: '{count} free · pinch to zoom, drag to move around',
  seats_count: 'Seats {count}',
  venue_will_add_seating: 'This table seats {seats}; the venue will add seating for {party}.',
  choose_table: 'Choose {table}',
  see_the_floor_plan: 'See the floor plan',
  tap_to_open_the_plan: 'Tap to open',
  tap_a_table_or_pick_below: 'Tap a table on the plan, or pick one below',
  free_tables: 'Free tables',
  checking_what_is_free: 'Checking what is free…',
  free_pick_where_you_sit: '{count} free — pick where you sit',
  seats_tap_to_change: 'Seats {seats} · tap to change',
  party_larger_extra_seating: 'A party of {count} is larger than any single table — the venue will arrange extra seating.',

  // ── coverage sweep ──
  youre_invited: "You're invited",

  // ── coverage sweep ──
  price_summary: 'Price Summary',

  // ── coverage sweep ──
  booking_summary: 'Booking Summary',

  // ── coverage sweep ──
  no_dates_in_the_next: 'No dates in the next few weeks',

  // ── coverage sweep ──
  guest_details: 'Guest Details',
  special_requests_optional: 'Special Requests (optional)',
  your_full_name: 'Your full name',

  // ── coverage sweep ──
  selected_date: 'Selected Date',
  change: 'Change',

  // ── coverage sweep ──
  the_venue_will_assign_the: 'The venue will assign the best available table for you',

  // ── coverage sweep ──
  special_offers: 'Special Offers',

  // ── coverage sweep ──
  notifications: 'Notifications',

  // ── coverage sweep ──
  gatheredhq: 'GatheredHQ',

  // ── coverage sweep ──
  enter_phone_at_checkout_to: 'Enter phone at checkout to earn points',
  loading_rewards: 'Loading rewards...',
  keep_earning_to_unlock_rewards: 'Keep earning to unlock rewards!',

  // ── coverage sweep ──
  popular: 'Popular',

  // ── coverage sweep ──
  browse_all_categories: 'Browse all categories',
  menu_categories: 'Menu categories',

  // ── coverage sweep ──
  categories_2: 'Categories',
  all_menu_categories: 'All menu categories',

  // ── coverage sweep ──
  customers_also_order: 'Customers also order 🔥',
  add_selections: 'Add Selections',

  // ── coverage sweep ──
  order_and_pay_online_well: "Order and pay online — we'll bring it straight to you",

  // ── coverage sweep ──
  or_pay_with_card: 'Or pay with card',
  processing: 'Processing...',

  // ── coverage sweep ──
  secure_payment: 'Secure payment',
  loading_secure_checkout: 'Loading secure checkout…',
  ziina_secure_checkout: 'Ziina secure checkout',
  close_payment: 'Close payment',

  // ── coverage sweep ──
  coming_soon: 'Coming Soon',

  // ── coverage sweep ──
  this_digital_menu_has_reached: 'This digital menu has reached its monthly view limit. Please ask a member of staff for assistance.',

  // ── coverage sweep ──
  visit_website: 'Visit website',
  send_email: 'Send email',

  // ── coverage sweep ──
  partly_paid: 'Partly Paid',

  // ── coverage sweep ──
  mark_read: 'Mark read',
  clear_all: 'Clear all',

  // ── coverage sweep ──
  venue_location_map: 'Venue Location Map',

  // ── coverage sweep ──
  lost_in_taste: 'Lost in Taste?',
  go_to_venue: 'Go to Venue',
  the_page_youre_looking_for: "The page you're looking for seems to have wandered off the menu. Try navigating back or scanning a nearby QR code.",

  // ── coverage sweep ──
  sign_in_with_email: 'Sign In with Email',

  // ── coverage sweep ──
  google: 'Google',

  // ── coverage sweep ──
  active_scanner: 'Active Scanner',
  ready_to_join: 'Ready to join?',
  position_the_qr_code_inside: 'Position the QR code inside the frame to automatically scan.',
  retry_access: 'Retry Access',
  cancel_scanning: 'Cancel Scanning',

  // ── coverage sweep ──
  open_in_google_maps: 'Open in Google Maps',

  // ── coverage sweep ──
  choose_a_menu_to_browse: 'Choose a menu to browse',
  no_menus_available: 'No menus available.',
  full_name: 'Full Name',
  phone_number: 'Phone Number',
  view_all: 'View All',
  you_just_added_the: 'You just added the',
  pay_now: 'Pay Now',
  crafted_with_care_by: 'Crafted with care by',
  crafted_by: 'Crafted by',
  dont_have_an_account: "Don't have an account?",

  // ── coverage sweep ──
  something_went_wrong: 'Something went wrong',
  stop_recording: 'Stop recording',
  voice_input: 'Voice input',

  // ── coverage sweep ──
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',

  // ── coverage sweep ──
  time: 'Time',
  days: 'Days',
  capacity: 'Capacity',
  happy_hour_special: 'Happy Hour Special',
  limited_time_offer: 'Limited Time Offer',

  // ── coverage sweep ──
  instagram: 'Instagram',
  facebook: 'Facebook',
  youtube: 'YouTube',
  x_twitter: 'X (Twitter)',

  // ── coverage sweep ──
  nice_and_relaxed: 'Nice and relaxed',
  vibrant_and_steady: 'Vibrant and steady',
  full_house: 'Full house',
  no_wait: 'No wait',
  ready: 'Ready',
  atmosphere_is_electric: 'Atmosphere is electric!',

  // ── coverage sweep ──
  sold_out_2: 'Sold out',
  ended: 'Ended',
  almost_gone: 'Almost gone',
  ends_today: 'Ends today',
  ends_tomorrow: 'Ends tomorrow',
  limited_time: 'Limited time',
  view_details: 'View details',
  book_now: 'Book Now',

  // ── shared across branches ──
  // A promotion the guest is seeing at one venue but which is HELD at another. The badge
  // names the host so nobody turns up at the wrong address.
  at_branch: 'At {venue}',
  opens_in_new_tab_notice: 'This opens in a new tab. Your order here stays open — just close the tab to come back.',

  // ── coverage sweep ──
  directions: 'Directions',
  call: 'Call',
  hide_password: 'Hide password',
  show_password: 'Show password',

  // ── coverage sweep ──
  mute: 'Mute',
  unmute: 'Unmute',

  // ── coverage sweep ──
  switch_to_light_theme: 'Switch to light theme',
  switch_to_dark_theme: 'Switch to dark theme',

  // ── coverage sweep ──
  redeem: 'Redeem',

  // ── coverage sweep ──
  optional: 'Optional',

  // ── coverage sweep ──
  active_order: 'Active Order',

  // ── coverage sweep ──
  by_items: 'By Items',
  equal: 'Equal',
  failed_to_start_payment: 'Failed to start payment',
  choose_your_items: 'Choose your items',
  full: 'Full',
  select_items_to_continue: 'Select items to continue',
  enter_an_amount_to_continue: 'Enter an amount to continue',

  // ── coverage sweep ──
  split_equally: 'Split Equally',
  by_item: 'By Item',
  guest: 'Guest',

  // ── coverage sweep ──
  payment_failed: 'Payment failed',

  // ── coverage sweep ──
  menu_temporarily_unavailable: 'Menu temporarily unavailable',

  // ── coverage sweep ──
  failed_to_place_order: 'Failed to place order',
  payment_setup_failed_please_try: 'Payment setup failed. Please try again.',
  room: 'Room',

  // ── coverage sweep ──
  redirecting: 'Redirecting...',

  // ── coverage sweep ──
  all: 'All',
  upcoming: 'Upcoming',
  reservation: 'Reservation',
  pending: 'Pending',
  dine_in_orders: 'Dine-in Orders',
  takeaway_orders: 'Takeaway Orders',
  all_your_orders: 'All Your Orders',
  room_service: 'Room Service',
  attended: 'Attended',

  // ── coverage sweep ──
  asia_dubai: 'Asia/Dubai',
  item_detail: 'Item Detail',
  show_less: 'Show less',
  more_details: 'More details',
  edit_note: 'Edit note',
  add_note: 'Add note',
  venue_closed: 'Venue Closed',
  select_options: 'Select options',

  // ── coverage sweep ──
  ready_for_pickup_2: 'Ready for Pickup',
  ready_to_serve: 'Ready to Serve',
  served: 'Served',
  failed_to_request_bill: 'Failed to request bill',
  confirming_payment: 'Confirming payment…',
  yes_cancel: 'Yes, Cancel',
  order_tracking: 'Order Tracking',
  this_order_could_not_be: 'This order could not be found.',
  your_order_has_been_served: 'Your order has been served — enjoy!',
  your_food_is_ready_waiter: 'Your food is ready! Waiter is on the way',
  card_apple_pay_google_pay: 'Card, Apple Pay, Google Pay',
  requesting: 'Requesting...',
  request_bill: 'Request Bill',
  pay_via_waiter_cash_card: 'Pay via waiter (cash, card, or QR)',
  this_order_cannot_be_cancelled: 'This order cannot be cancelled.',
  kitchen_has_started_preparing_cancellation: 'Kitchen has started preparing — cancellation is no longer available.',
  order_can_no_longer_be: 'Order can no longer be cancelled.',

  // ── coverage sweep ──
  failed_to_load_payment: 'Failed to load payment',
  you: 'You',
  no_orders_found: 'No orders found',
  your_bill_is: 'Your bill is',
  you_paid: 'You paid',
  fully_paid: 'Fully paid',
  redirecting_2: 'Redirecting…',
  pay_in_full: 'Pay in Full',

  // ── coverage sweep ──
  failed_to_submit_review: 'Failed to submit review',

  // ── coverage sweep ──
  camera_permission_denied_please_allow: 'Camera permission denied. Please allow camera access.',
  could_not_start_camera_please: 'Could not start camera. Please check your device settings.',

  // ── coverage sweep ──
  pay_the_table_bill: 'Pay the table bill',
  payment_due: 'Payment Due',
  order: 'Order',

  // ── coverage sweep ──
  failed_to_create_booking: 'Failed to create booking',
  area_charge: 'Area charge',
  deposit_due_now: 'Deposit Due Now',
  total_due: 'Total Due',

  // ── coverage sweep ──
  failed_to_load_promotion: 'Failed to load promotion',
  reserve_now: 'Reserve Now',
  choose_a_date: 'Choose a date',
  add_at_least_one_guest: 'Add at least one guest',
  too_many_guests_for_this: 'Too many guests for this date',
  enter_a_valid_email: 'Enter a valid email',
  enter_a_valid_phone_number: 'Enter a valid phone number',
  accept_the_terms_to_continue: 'Accept the terms to continue',
  heres_what_to_expect: 'Here’s what to expect',
  good_to_know: 'Good to know',
  everything_youll_love_about_it: 'Everything you’ll love about it',
  about_this_offer: 'About this offer',
  full_refund: 'Full refund',
  holding_your_table: 'Holding your table…',
  continue_to_checkout: 'Continue to Checkout',

  // ── coverage sweep ──
  awaiting_payment: 'Awaiting Payment',
  cancelled: 'Cancelled',
  no_show: 'No Show',
  failed_to_load_booking: 'Failed to load booking',
  booking_pass: 'Booking Pass',
  we_cant_wait_to_host: 'We can’t wait to host you.',

  // ── coverage sweep ──
  failed_to_load_promotions: 'Failed to load promotions',
  browse_upcoming_events: 'Browse upcoming events',
  event_ended: 'Event Ended',
  view_details_2: 'View Details',

  // ── order tracking ──
  // `{count}` is substituted by `t(key, { count })`. Keep the placeholder inside the
  // sentence rather than concatenating around it: Arabic and Japanese do not put the
  // number where English does, and a translator can only move what is in their string.
  pay_all_orders_together: 'Pay all {count} orders together',
  request_bill_for_all_orders: 'Request bill for all {count} orders',
  payment_received_confirming: 'Payment received! Confirming your order…',
  payment_cancelled_nothing_charged: 'Payment was cancelled — nothing was charged. You can try again anytime.',
  bill_requested_for_all_orders: 'Bill requested for all orders! Your waiter will be with you shortly.',
  order_cancelled_nothing_charged: 'Order cancelled. Nothing was charged.',
  no_date: 'No Date',

  // ── promotion types ──
  // `PROMOTION_TYPE_LABELS` is shared with the staff and dashboard apps, which are
  // English-only; the guest app resolves the same types through these keys instead.
  promo_type_bundle: 'Bundle Deal',
  promo_type_dish_of_day: 'Dish of the Day',
  promo_type_happy_hour: 'Happy Hour',
  promo_type_discount: 'Discount',
  promo_type_custom: 'Custom Offer',
  promo_type_buffet: 'Buffet',

  // ── short labels ──
  // One-word badges and column headings. They stayed English because the coverage
  // scanner demanded six characters of a JSX text node, so `<span>All</span>` and every
  // label like it were invisible to it.
  best: 'Best',
  from: 'From',
  more_dates: 'More dates',
  save: 'Save',
  on_your_total: 'on your total',
  note: 'Note',
  any: 'Any',
  skip: 'Skip',
  clear: 'Clear',
  paid: 'Paid',
  add: 'Add',
  pay: 'Pay',
  retry: 'Retry',
  date: 'Date',
  share: 'Share',
  terms: 'Terms',
  email: 'Email',
  live: 'Live',
  auto: 'Auto',
} as const;

/**
 * Venue PROFILE fields, stored alongside the UI strings but not part of the catalogue.
 *
 * A venue's name and address are the owner's own words, not app copy — "Al Safadi" is not
 * a string anyone can look up, and a machine translation of a brand is a liability. So
 * these two keys are reserved in `venue_ui_translations` (whose `string_key` is free
 * text) and are written BY THE OWNER, per language, never by the translation sweep.
 *
 * They are deliberately outside `UIStringKey`: `t()` must not resolve them, because a
 * venue that has not filled them in has to fall back to the real venue record, not to a
 * catalogue default.
 */
export const VENUE_PROFILE_TRANSLATION_KEYS = {
  name: 'venue_name',
  address: 'venue_address',
} as const;

export type VenueProfileField = keyof typeof VENUE_PROFILE_TRANSLATION_KEYS;

export type UIStringKey = keyof typeof UI_STRING_KEYS;

/** Map of string_key → translated value for a single locale */
export type UIStringsForLocale = Partial<Record<UIStringKey, string>>;

/** Map of locale → (string_key → translated value) */
export type UITranslationsMap = Record<string, UIStringsForLocale>;

/** Grouped keys for the dashboard translation panel */
export const UI_STRING_GROUPS = {
  'Landing Page': ['welcome_to', 'browse_menu', 'ready_to_order', 'our_menus', 'browse_by_category', 'quick_actions', 'our_location', 'get_in_touch', 'checked_in', 'venue_location', 'view_active_orders', 'order_in_progress', 'ready_for_pickup', 'track', 'membership', 'track_your_history', 'welcome_back', 'sign_in', 'profile'],
  'Menu': ['menu', 'discover_collection', 'search_placeholder', 'no_items_found', 'no_items_found_desc', 'clear_filters', 'items'],
  'Item Detail': ['the_experience', 'select_option', 'special_requests', 'special_requests_placeholder', 'add_to_order', 'view_cart', 'swipe_for_more', 'chefs_choice', 'item_not_found', 'return_to_menu', 'customize', 'required', 'select_at_least', 'select_up_to', 'included_free', 'selected', 'free'],
  'Cart': ['your_cart', 'cart_empty', 'cart_empty_desc', 'browse_menu_cta', 'subtotal', 'service_fee', 'total', 'proceed_to_checkout'],
  'Checkout': ['checkout', 'order_type', 'dine_in', 'counter_pickup', 'table_number', 'table_number_placeholder', 'auto_detected_qr', 'your_name', 'your_name_placeholder', 'your_phone', 'your_phone_placeholder', 'order_notes', 'order_notes_placeholder', 'order_summary', 'continue_to_payment'],
  'Payment': ['payment', 'bill_summary', 'add_a_tip', 'tip', 'tip_added', 'custom', 'payment_method', 'pay_at_counter', 'pay_at_counter_desc', 'pay_online', 'pay_online_desc', 'pay_on_delivery', 'pay_on_delivery_desc', 'place_order', 'placing_order', 'proceed_to_pay', 'setting_up_payment'],
  'Order Tracking': ['your_order', 'order_received', 'confirmed', 'preparing', 'completed', 'order_cancelled', 'order_complete', 'kitchen', 'bar', 'queue_position', 'estimated_wait', 'updates_automatically', 'rate_your_experience', 'collect_at_counter', 'partially_ready'],
  'Orders': ['active', 'past', 'no_active_orders', 'no_past_orders', 'place_order_to_track', 'completed_orders_here', 'track_order'],
  'Review': ['how_was_experience', 'sorry_to_hear', 'thanks_feedback', 'glad_you_enjoyed', 'what_did_you_love', 'what_could_be_better', 'additional_comments', 'comments_placeholder', 'submit_review', 'thank_you', 'feedback_helps', 'rate_on_google', 'back_to_home'],
  'AI Assistant': ['ai_menu_guide', 'ai_ask_anything', 'what_are_you_craving', 'ai_help_text', 'ai_input_placeholder', 'items_in_cart'],
  'Navigation': ['home', 'orders', 'history', 'map', 'ai_guide'],
  'Profile': ['sign_in', 'sign_out', 'profile', 'order_history', 'view_past_orders', 'alerts', 'notifications_promotions', 'preferences', 'theme', 'dark_mode', 'light_mode', 'dark', 'light', 'language', 'menu_interface_language', 'why_sign_in', 'track_order_history', 'manage_bookings', 'personalized_offers', 'faster_checkout', 'continue_with_google', 'signing_in'],
  'About & Support': ['about_us', 'contact', 'follow_us', 'need_help', 'contact_support', 'powered_by', 'venue_support', 'platform_support', 'platform_support_desc', 'common_questions', 'faq_order_q', 'faq_order_a', 'faq_modify_q', 'faq_modify_a', 'faq_payment_q', 'faq_payment_a', 'venue_email', 'no_venue_email', 'tap_to_call', 'wifi_not_available', 'need_help_desc'],
  'Alerts & History': ['no_notifications', 'notifications_will_appear', 'just_now', 'recent_orders', 'history_empty_signed_out', 'history_empty', 'address', 'address_not_available', 'venue_map', 'get_directions'],
  'Common': ['browse', 'back', 'close', 'loading', 'go_back', 'go_home', 'try_again', 'error_loading_menu', 'venue_not_found', 'open', 'closed', 'mins', 'kcal', 'wifi', 'support', 'find_us', 'about', 'guest_wifi', 'network_name', 'password', 'available_for_calls', 'no_phone', 'address_not_set', 'self_service', 'waiter_service', 'hybrid_service', 'table', 'contains', 'upgrade_to_add'],
  'Cart & Checkout': ['items_unavailable_title', 'items_unavailable_hint', 'out_of_stock', 'remove_unavailable_items', 'remove', 'special_request', 'special_request_placeholder', 'orders_not_accepted_now', 'privacy_policy'],
  'Order Mode': ['select_preferred_option', 'dine_in_desc', 'takeaway_desc', 'whats_your_table_number', 'check_table_card', 'continue_action', 'enter_table_later'],

} as const satisfies Record<string, readonly UIStringKey[]>;

export type UIStringGroup = keyof typeof UI_STRING_GROUPS;
